import { FormField } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";
import { memo, useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { PeriodSchedule } from "./PeriodSchedule";
import { filterNextPeriodStartOptions, DAYS } from "./types";
import { type DayScheduleProps } from "./types";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";

export const DaySchedule = memo(function DaySchedule({ day }: DayScheduleProps) {
  const form = useFormContext();

  // Use useWatch for more efficient form value watching
  const openingDays = useWatch({
    control: form.control,
    name: "openingDays",
  });

  const isDaySelected = useMemo(() => {
    return openingDays?.includes(day.id) || false;
  }, [openingDays, day.id]);

  const handleDayToggle = useCallback(
    (checked: boolean) => {
      const currentDays = form.getValues("openingDays") || [];
      if (checked) {
        form.setValue("openingDays", [...currentDays, day.id], { shouldValidate: true, shouldDirty: true });
      } else {
        form.setValue(
          "openingDays",
          currentDays.filter((d: string) => d !== day.id),
          { shouldValidate: true, shouldDirty: true }
        );
      }
    },
    [form, day.id]
  );

  const handleCopyFromDay = useCallback(
    (sourceDayId: string) => {
      const sourceDayConfig = form.getValues(`openingHours.${sourceDayId}`);
      if (sourceDayConfig) {
        form.setValue(`openingHours.${day.id}`, sourceDayConfig, { shouldDirty: true, shouldValidate: true });
      }
    },
    [form, day.id]
  );

  // Watch for changes in period end times to update next period options
  const matinEndTime = useWatch({
    control: form.control,
    name: `openingHours.${day.id}.matin.end`,
  });

  const midiEndTime = useWatch({
    control: form.control,
    name: `openingHours.${day.id}.midi.end`,
  });

  const midiActive = useWatch({
    control: form.control,
    name: `openingHours.${day.id}.midi.active`,
  });

  // Ensure coherence between periods by filtering start times for each period
  const midiStartTimeOptions = filterNextPeriodStartOptions(matinEndTime);

  // If midi is not active, afternoon should start after morning ends
  // If midi is active, afternoon should start after midi ends
  const apresStartTimeOptions = midiActive ? filterNextPeriodStartOptions(midiEndTime) : filterNextPeriodStartOptions(matinEndTime);

  const availableDaysToCopy = useMemo(() => {
    return openingDays?.filter((d: string) => d !== day.id) || [];
  }, [openingDays, day.id]);

  const availableDaysCount = availableDaysToCopy.length;

  return (
    <div key={day.id} className="space-y-6 rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between space-x-3">
        <label htmlFor={day.id} className="flex flex-1 cursor-pointer items-center space-x-3">
          <FormField
            control={form.control}
            name="openingDays"
            render={() => <Checkbox id={day.id} checked={isDaySelected} onCheckedChange={handleDayToggle} className="h-5 w-5" />}
          />
          <span className="text-lg font-semibold text-gray-900 select-none">{day.label}</span>
        </label>

        {isDaySelected && availableDaysCount > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="ml-auto text-sm text-blue-500 underline transition-colors hover:text-blue-700 hover:cursor-pointer">Copier d'un autre jour</button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {availableDaysToCopy.map((sourceDayId: string) => {
                const sourceDayLabel = DAYS.find((d) => d.id === sourceDayId)?.label || sourceDayId;
                return (
                  <DropdownMenuItem key={sourceDayId} onClick={() => handleCopyFromDay(sourceDayId)} className="cursor-pointer">
                    {sourceDayLabel}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isDaySelected && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <PeriodSchedule day={day.id} periodKey="matin" periodLabel="Matin" />
          <PeriodSchedule day={day.id} periodKey="midi" periodLabel="Midi" startTimeOptions={midiStartTimeOptions} />
          <PeriodSchedule day={day.id} periodKey="apres" periodLabel="Aprèm" startTimeOptions={apresStartTimeOptions} />
        </div>
      )}
    </div>
  );
});
